/**
 * OrganizationSettings Component
 * Main container for organization management interface
 */
import { useState, useEffect } from 'react';
import { organizationsApi, Organization, OrganizationStats, MemberWithQuota, Invitation, CreateInvitationRequest } from '../../api/organizations';
import { useAuthStore } from '../../stores/authStore';
import OrganizationStatsPanel from './OrganizationStatsPanel';
import MemberList from './MemberList';
import InvitationList from './InvitationList';
import InviteByEmailModal from './InviteByEmailModal';
import InviteByQRCodeModal from './InviteByQRCodeModal';
import EditQuotaModal from './EditQuotaModal';
import ConfirmRemoveMemberModal from './ConfirmRemoveMemberModal';
import OrganizationSetup from './OrganizationSetup';

// Simple SVG chevron icon component
const ChevronDownIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);

interface OrganizationSettingsProps {
  userId: string; // Current user ID
}

type ActiveTab = 'members' | 'invitations';

export default function OrganizationSettings({ userId }: OrganizationSettingsProps) {
  // Get user info from auth store
  const { user } = useAuthStore();

  // State
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const [members, setMembers] = useState<MemberWithQuota[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>('members');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);

  // Modal states
  const [showInviteEmailModal, setShowInviteEmailModal] = useState(false);
  const [showInviteQRModal, setShowInviteQRModal] = useState(false);
  const [showInviteDropdown, setShowInviteDropdown] = useState(false);
  const [editingMember, setEditingMember] = useState<MemberWithQuota | null>(null);
  const [removingMember, setRemovingMember] = useState<MemberWithQuota | null>(null);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Load initial data
  useEffect(() => {
    loadAllData();
  }, []);

  // Toast auto-dismiss
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    setNeedsSetup(false);

    try {
      // First get the organization
      const orgData = await organizationsApi.getMyOrganization();
      setOrganization(orgData);

      // Then fetch related data using the organization ID
      const [statsData, membersData, invitationsData] = await Promise.all([
        organizationsApi.getOrganizationStats(orgData.id).catch(() => null),
        organizationsApi.listMembers(orgData.id, true).catch(() => []),
        organizationsApi.listInvitations(undefined, true).catch(() => []),
      ]);

      setStats(statsData);
      setMembers(membersData);
      setInvitations(invitationsData);
    } catch (err: any) {
      console.error('Failed to load organization data:', err);

      // Check if it's a 404 error (user has no organization)
      if (err.response?.status === 404 && err.response?.data?.detail?.error_code === 'ORG003') {
        // User has org tier but no organization - show setup
        setNeedsSetup(true);
      } else {
        setError(err.response?.data?.detail?.message || err.message || 'Failed to load organization data');
      }
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
  };

  // Handle invite member
  const handleInvite = async (data: CreateInvitationRequest) => {
    const result = await organizationsApi.createInvitation(data);
    await loadAllData(); // Refresh all data
    return result;
  };

  // Handle edit quota
  const handleEditQuota = async (userId: string, quotas: any) => {
    if (!organization) return;
    await organizationsApi.updateSubuserQuota(organization.id, userId, quotas);
    await loadAllData();
    showToast('Member quotas updated successfully');
  };

  // Handle remove member
  const handleRemoveMember = async () => {
    if (!organization || !removingMember) return;
    await organizationsApi.removeSubuser(organization.id, removingMember.member.id);
    await loadAllData();
    showToast('Member removed successfully');
  };

  // Handle cancel invitation
  const handleCancelInvitation = async (invitationId: string) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) return;

    try {
      await organizationsApi.cancelInvitation(invitationId);
      await loadAllData();
      showToast('Invitation canceled');
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to cancel invitation', 'error');
    }
  };

  // Handle copy invitation link
  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast('Invitation link copied to clipboard');
    } catch (err) {
      showToast('Failed to copy link', 'error');
    }
  };

  // Calculate available quotas from tier limits
  const getAvailableQuotas = () => {
    if (!organization || !stats) {
      return { slots: 0, conversations: 0, aiRequests: 0 };
    }

    return {
      slots: stats.available_slots,
      conversations: stats.conversations_limit,
      aiRequests: stats.ai_requests_limit,
    };
  };

  // Setup state - user has org tier but no organization
  if (needsSetup && !loading) {
    return <OrganizationSetup onOrganizationCreated={loadAllData} userTier={user?.subscription_tier} />;
  }

  // Error state
  if (error && !organization) {
    return (
      <div className="p-8 space-y-6">
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-16 h-16 mb-4 flex items-center justify-center text-red-500">
            <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-dark-text mb-2">Failed to Load Organization</h3>
          <p className="text-sm text-dark-text-muted text-center max-w-md mb-6">{error}</p>
          <button
            onClick={loadAllData}
            className="px-6 py-3 bg-gradient-to-r from-accent-blue to-accent-orange
                     text-white font-semibold rounded-xl
                     hover:shadow-lg hover:shadow-accent-blue/50
                     transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-semibold text-dark-text mb-2">Organization Settings</h3>
        {organization && (
          <div className="flex items-center gap-4 text-sm text-dark-text-muted">
            <span className="font-medium text-dark-text">{organization.name}</span>
            <span>•</span>
            <span>ID: {organization.id.substring(0, 8)}...</span>
            <span>•</span>
            <span>Tier: {organization.subscription_tier}</span>
          </div>
        )}
      </div>

      {/* Stats Panel */}
      <OrganizationStatsPanel stats={stats} loading={loading} />

      {/* Actions bar with invite button */}
      <div className="flex items-center justify-between">
        <div className="relative">
          <button
            onClick={() => setShowInviteDropdown(!showInviteDropdown)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-accent-blue to-accent-orange
                     text-white font-semibold rounded-xl
                     hover:shadow-lg hover:shadow-accent-blue/50
                     transition-all"
            disabled={!stats || stats.available_slots === 0}
          >
            Invite Member
            <ChevronDownIcon className="w-4 h-4" />
          </button>

          {showInviteDropdown && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowInviteDropdown(false)}
              />
              <div className="absolute top-full left-0 mt-2 w-48 bg-dark-card border border-dark-border rounded-lg shadow-xl overflow-hidden z-20">
                <button
                  onClick={() => {
                    setShowInviteEmailModal(true);
                    setShowInviteDropdown(false);
                  }}
                  className="w-full px-4 py-3 text-left text-dark-text hover:bg-dark-bg transition-colors"
                >
                  By Email
                </button>
                <button
                  onClick={() => {
                    setShowInviteQRModal(true);
                    setShowInviteDropdown(false);
                  }}
                  className="w-full px-4 py-3 text-left text-dark-text hover:bg-dark-bg transition-colors border-t border-dark-border"
                >
                  By QR Code
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-dark-border/50">
        <nav className="flex gap-6">
          <button
            onClick={() => setActiveTab('members')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'members'
                ? 'border-accent-blue text-accent-blue'
                : 'border-transparent text-dark-text-muted hover:text-dark-text'
            }`}
          >
            Members ({members.length})
          </button>
          <button
            onClick={() => setActiveTab('invitations')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'invitations'
                ? 'border-accent-blue text-accent-blue'
                : 'border-transparent text-dark-text-muted hover:text-dark-text'
            }`}
          >
            Pending Invitations ({invitations.length})
          </button>
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'members' && (
          <MemberList
            members={members}
            currentUserId={userId}
            ownerId={organization?.owner_id || ''}
            loading={loading}
            onEditQuota={setEditingMember}
            onRemoveMember={setRemovingMember}
            onRefresh={loadAllData}
          />
        )}

        {activeTab === 'invitations' && (
          <InvitationList
            invitations={invitations}
            loading={loading}
            onCancelInvitation={handleCancelInvitation}
            onCopyLink={handleCopyLink}
          />
        )}
      </div>

      {/* Modals */}
      <InviteByEmailModal
        isOpen={showInviteEmailModal}
        onClose={() => setShowInviteEmailModal(false)}
        organizationId={organization?.id || ''}
        availableQuotas={getAvailableQuotas()}
        onInvite={handleInvite}
        onSuccess={() => showToast('Invitation sent successfully')}
      />

      <InviteByQRCodeModal
        isOpen={showInviteQRModal}
        onClose={() => setShowInviteQRModal(false)}
        organizationId={organization?.id || ''}
        availableQuotas={getAvailableQuotas()}
        onInvite={handleInvite}
        onSuccess={() => showToast('QR code generated successfully')}
      />

      <EditQuotaModal
        isOpen={!!editingMember}
        onClose={() => setEditingMember(null)}
        member={editingMember}
        organizationId={organization?.id || ''}
        availableQuotas={getAvailableQuotas()}
        onUpdate={handleEditQuota}
        onSuccess={() => showToast('Quotas updated successfully')}
      />

      <ConfirmRemoveMemberModal
        isOpen={!!removingMember}
        onClose={() => setRemovingMember(null)}
        member={removingMember}
        organizationId={organization?.id || ''}
        onConfirm={handleRemoveMember}
      />

      {/* Toast notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-slide-up">
          <div
            className={`px-6 py-4 rounded-lg shadow-xl border ${
              toast.type === 'success'
                ? 'bg-green-500/20 border-green-500/50 text-green-300'
                : 'bg-red-500/20 border-red-500/50 text-red-300'
            }`}
          >
            <p className="text-sm font-medium">{toast.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}
